// Video tab: the product's videos — YouTube links, the same in every language, shown in its gallery in this
// order — one of them the main one, its trailer (the book page's trailer button plays it). A link is added at
// the top; each video can be made the main one, moved, or taken out.
import React, { useState } from "react"
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, StarIcon as StarOutline, XMarkIcon } from "@heroicons/react/24/outline"
import { StarIcon } from "@heroicons/react/24/solid"
import Button from "aldoo-ra/Button"
import { TextInput } from "../../../AdminShared/ui"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { getYoutubeId } from "../shared/editor-config"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const MAX = 20

/** The list as kept: each once, one main (the marked one, else the first). */
const tidy = (list) => {
  const seen = new Set()
  const out = list.filter((video) => {
    const id = getYoutubeId(video.url)
    if (!id || seen.has(id)) return false
    seen.add(id)
    return true
  })
  const main = out.findIndex((video) => video.main)
  return out.map((video, index) => ({ url: video.url, main: index === (main >= 0 ? main : 0) }))
}

export default function TrailerTab({ editor, isProductEditor }) {
  const { read, set } = editor
  const t = useProductEditorTexts()
  const [draft, setDraft] = useState("")
  const [error, setError] = useState("")
  // a product from before the videos has its trailer only: shown as its main video until the list is edited
  const stored = read("videos")
  const trailer = read("bookTrailer")
  // (a list, even an emptied one, is the product's own: the trailer is not brought back once its video is removed)
  const videos = Array.isArray(stored) ? stored : trailer && getYoutubeId(trailer) ? [{ url: trailer, main: true }] : []
  const write = (list) => set("videos", tidy(list))

  const add = () => {
    const url = draft.trim()
    if (!url) return
    const id = getYoutubeId(url)
    if (!id) return setError(t`Enter a YouTube link`)
    if (videos.some((video) => getYoutubeId(video.url) === id)) return setError(t`This video is in the list already`)
    if (videos.length >= MAX) return setError(t("%s videos at most", MAX))
    write([...videos, { url, main: !videos.length }])
    setDraft("")
    setError("")
  }
  const move = (index, by) => {
    const next = [...videos]
    const [video] = next.splice(index, 1)
    next.splice(index + by, 0, video)
    write(next)
  }

  return (
    <div className="flex flex-col gap-4 max-w-[760px]" data-testid="videos-tab">
      <p className="m-0 text-sm text-admin_text_secondary inline-flex items-center gap-1.5">{t`YouTube videos of the product, shown in its gallery in this order. The main one is its trailer.`}<HelpHint helpKey="vendor.product#trailer" /></p>

      {isProductEditor && (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <TextInput
              value={draft}
              onChange={(value) => (setDraft(value), setError(""))}
              onKeyDown={(event) => event.key === "Enter" && (event.preventDefault(), add())}
              placeholder="https://www.youtube.com/watch?v=…"
              className="flex-1 min-w-0"
              data-testid="video-add-url"
            />
            <Button variant="admin_secondary" onClick={add} disabled={!draft.trim()} data-testid="video-add">
              <PlusIcon className="w-4 h-4" />
              {t`Add`}
            </Button>
          </div>
          {error && <span className="text-xs text-admin_error" role="alert">{error}</span>}
        </div>
      )}

      {videos.length ? (
        <ol className="m-0 p-0 list-none flex flex-col gap-3" data-testid="videos-list">
          {videos.map((video, index) => {
            const id = getYoutubeId(video.url)
            return (
              <li key={id} className={`flex flex-col sm:flex-row gap-3 rounded-lg border p-3 ${video.main ? "border-admin_primary bg-admin_primary/5" : "border-admin_bordered"}`} data-testid="video-item">
                <div className="w-full sm:w-[240px] shrink-0 aspect-video bg-black rounded-md overflow-hidden">
                  <iframe title={video.url} className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    {video.main && <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-admin_primary text-white text-xs font-semibold" data-testid="video-main">{t`Main video`}</span>}
                    <a href={video.url} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate text-sm text-admin_text hover:text-admin_primary">{video.url}</a>
                  </div>
                  {isProductEditor && (
                    <div className="flex flex-wrap items-center gap-1">
                      {!video.main && (
                        <Button variant="admin_text" className="h-8 px-2 text-xs" onClick={() => write(videos.map((one, at) => ({ ...one, main: at === index })))} data-testid="video-make-main">
                          <StarOutline className="w-4 h-4" />
                          {t`Make it the main video`}
                        </Button>
                      )}
                      {video.main && (
                        <span className="inline-flex items-center gap-1 h-8 px-2 text-xs text-admin_text_secondary">
                          <StarIcon className="w-4 h-4 text-admin_primary" />
                          {t`The trailer`}
                        </span>
                      )}
                      <span className="flex-1" />
                      <button type="button" disabled={index === 0} onClick={() => move(index, -1)} className="p-1.5 rounded hover:bg-admin_neutral_1 disabled:opacity-30" title={t`Move up`} aria-label={t`Move up`}>
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button type="button" disabled={index === videos.length - 1} onClick={() => move(index, 1)} className="p-1.5 rounded hover:bg-admin_neutral_1 disabled:opacity-30" title={t`Move down`} aria-label={t`Move down`}>
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => write(videos.filter((_, at) => at !== index))} className="p-1.5 rounded text-admin_text_secondary hover:text-admin_error hover:bg-admin_neutral_1" title={t`Remove`} aria-label={t`Remove`} data-testid="video-remove">
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      ) : (
        <div className="text-sm text-admin_text_secondary italic">{t`No videos yet`}</div>
      )}
    </div>
  )
}
